import React, { useState, useEffect } from 'react';
import {
  Modal,
  Steps,
  Form,
  Input,
  Button,
  Select,
  message,
  Spin,
  Card,
  Typography,
  Space,
  Alert,
  Upload,
  Row,
  Col,
  Checkbox
} from 'antd';
import {
  CloseOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { Lead } from '../services/api';
import apiService from '../services/api';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { fetchWithCsrf } from '../utils/csrf';
import { mergeTemplateFieldValues, hasMeaningfulValue } from '../utils/fieldMapping';

const { Step } = Steps;
const { Option } = Select;
const { Title, Text } = Typography;

interface NOSSecuritasDocumentWizardProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  lead: Lead | null;
}

const NOS_SECURITAS_DOC_TEMPLATES = [
    { name: 'contrato_adesao_nos_securitas', display_name: 'Contrato de Adesão NOS' }
];

// Fix EMAIL_DEFAULT type
type EmailStepType = {
  subject: string;
  body: string;
  morada: string;
  contacto: string;
  documentos: string[];
  notas: string;
};

const EMAIL_DEFAULT: EmailStepType = {
  subject: 'Documentos para assinatura e instruções',
  body: '',
  morada: '',
  contacto: '',
  documentos: [],
  notas: ''
};

const EMAIL_DOC_OPTIONS = [
  'Assinar o FA no TABLET',
  'Assinar PROC no TABLET',
  'Assinar PROC em PAPEL e anexar na ASSANA',
  'Assinar portabilidade móvel em PAPEL e anexar na ASSANA',
  'Assinar portabilidade fixa em PAPEL e anexar na ASSANA',
  'Anexar Cópia do cartão de cidadão do titular no TABLET',
  'Anexar cópia do cartão de cidadão em ASSANA'
];

const NOSSecuritasDocumentWizard: React.FC<NOSSecuritasDocumentWizardProps> = ({ open, onClose, onSave, lead }) => {
  const [step, setStep] = useState(0);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const [allFields, setAllFields] = useState<Record<string, any[]>>({});
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentPages, setDocumentPages] = useState<Record<string, any[]>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [emailStep, setEmailStep] = useState<EmailStepType>({ ...EMAIL_DEFAULT });
  const [emailFiles, setEmailFiles] = useState<File[]>([]);
  const [emailPreview, setEmailPreview] = useState('');
  const [sending, setSending] = useState(false);
  const [availableDocs, setAvailableDocs] = useState<any[]>([]);
  const [form] = Form.useForm();

  // Load field values on open — merge into existing state (no wipe)
  useEffect(() => {
    if (open && lead?.id) {
      apiService.getFieldValues(lead.id).then((res) => {
        if (res.success) {
          const fv = res.field_values || {};
          setFieldValues((prev) => ({ ...prev, ...fv }));
        }
      });
    }
  }, [open, lead]);

  // Auto-check mandatory equipment when fields are loaded
  useEffect(() => {
    if (!allFields['contrato_adesao_nos_securitas']?.length) return;
    setFieldValues((prev) => {
      const mandatoryFields = [
        'equipamento_obrigatorio_detetor_movimento',
        'equipamento_obrigatorio_detetor_movimento_camera',
        'equipamento_obrigatorio_painel_controlo',
        'equipamento_obrigatorio_placa_dissuasora',
        'equipamento_obrigatorio_sensor_porta_janela'
      ];
      const next = { ...prev };
      let hasChanges = false;
      mandatoryFields.forEach((fieldName) => {
        if (!hasMeaningfulValue(prev[fieldName]) || prev[fieldName] === false) {
          next[fieldName] = true;
          hasChanges = true;
        }
      });
      return hasChanges ? next : prev;
    });
  }, [allFields]);

  // Autofill: saved + in-session + lead + related PDF fields
  useEffect(() => {
    if (!open || !lead || Object.keys(allFields).length === 0) return;
    setFieldValues((prev) =>
      mergeTemplateFieldValues(Object.values(allFields).flat(), lead, prev)
    );
  }, [open, lead?.id, lead?.name, lead?.dif, lead?.address, lead?.phone, lead?.email, lead?.city, lead?.postal_code, allFields, selectedTemplates]);

  // Save on page change
  useEffect(() => {
    if (step === 1 && open && lead?.id) {
      const timeoutId = setTimeout(async () => {
        // Only save if there are field values to save
        if (Object.keys(fieldValues).length > 0) {
          await apiService.saveFieldValues(lead.id, fieldValues);
          if (selectedTemplates.length > 0) {
            const currentTemplate = selectedTemplates[currentDocumentIndex];
            await apiService.saveDocumentProgress(lead.id, currentTemplate, currentPage, false);
          }
        }
      }, 1000); // Debounce for 1 second
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentPage, step, open, lead?.id, fieldValues, selectedTemplates, currentDocumentIndex, currentPage]);

  useEffect(() => {
    if (open) {
      setStep(0);
      setSelectedTemplates([]);
      setCurrentDocumentIndex(0);
      setAllFields({});
      setFieldValues({});
      setError(null);
      setDocumentPages({});
      setCurrentPage(1);
      setEmailStep({ ...EMAIL_DEFAULT });
      setEmailFiles([]);
      setEmailPreview('');
      setSending(false);
    }
  }, [open]);

  // Fetch available documents for the lead when opening or entering email step
  useEffect(() => {
    if (open && lead?.id) {
      apiService.getLeadDocuments(lead.id).then(res => {
        if (res.success) {
          setAvailableDocs(res.documents || []);
        }
      });
    }
  }, [open, lead]);

  // Load NOS Securitas fields and pages
  const loadFields = async (templateNames: string[]): Promise<Record<string, any[]>> => {
    setLoading(true);
    try {
      const fieldsMap: Record<string, any[]> = {};
      for (const templateName of templateNames) {
        const data = await apiService.getVodafoneTemplateFields(templateName);
        fieldsMap[templateName] = data;
      }
      setAllFields(fieldsMap);
      return fieldsMap;
    } catch (e) {
      setError('Erro ao carregar os campos do documento.');
      return {};
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentPages = async (
    templateNames: string[],
    fieldsMap: Record<string, any[]>
  ) => {
    setLoading(true);
    try {
      const pagesMap: Record<string, any[]> = {};
      for (const templateName of templateNames) {
        try {
          const response = await apiService.getDocumentPages(templateName);
          if (response.pages && response.pages.length > 0) {
            pagesMap[templateName] = response.pages;
          } else {
            // fallback: single page with all fields
            const fields = fieldsMap[templateName] || [];
            pagesMap[templateName] = [
              {
                page_number: 1,
                page_name: 'Campos',
                page_description: 'Todos os campos do documento',
                field_names: fields.map(f => f.name)
              }
            ];
          }
        } catch (e) {
          const fields = fieldsMap[templateName] || [];
          pagesMap[templateName] = [
            {
              page_number: 1,
              page_name: 'Campos',
              page_description: 'Todos os campos do documento',
              field_names: fields.map(f => f.name)
            }
          ];
        }
      }
      setDocumentPages(pagesMap);
    } catch (e) {
      setError('Erro ao carregar as páginas do documento.');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (values: string[]) => {
    setSelectedTemplates(values);
    setAllFields({});
  };

  const handleNextStep = async () => {
    if (step === 0 && selectedTemplates.length > 0) {
      setLoading(true);
      const fieldsMap = await loadFields(selectedTemplates);
      await loadDocumentPages(selectedTemplates, fieldsMap);
      let savedFv: Record<string, any> = {};
      if (lead?.id) {
        try {
          const res = await apiService.getFieldValues(lead.id);
          if (res.success && res.field_values) savedFv = res.field_values;
        } catch {
          /* ignore */
        }
      }
      const flat = Object.values(fieldsMap).flat();
      setFieldValues((prev) => mergeTemplateFieldValues(flat, lead, { ...prev, ...savedFv }));
      setLoading(false);
      setStep(1);
    }
  };

  const handlePreviousStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleFieldChange = (name: string, value: any) => {
    setFieldValues((prev) => {
      const newValues = { ...prev, [name]: value };
      // Autofill related fields
      Object.values(allFields).flat().forEach(field => {
        if (field.related_fields && field.related_fields.includes(name) && field.name !== name) {
          newValues[field.name] = value;
        }
      });
      return newValues;
    });
  };

  const getCurrentTemplate = () => selectedTemplates[currentDocumentIndex];
  const getCurrentFields = () => {
    const currentTemplate = getCurrentTemplate();
    return currentTemplate ? allFields[currentTemplate] || [] : [];
  };

  // Save when moving from Campos to next step
  const handleNextDocument = async () => {
    if (lead?.id && Object.keys(fieldValues).length > 0) {
      await apiService.saveFieldValues(lead.id, fieldValues);
      if (selectedTemplates.length > 0) {
        const currentTemplate = selectedTemplates[currentDocumentIndex];
        await apiService.saveDocumentProgress(lead.id, currentTemplate, currentPage, false);
      }
    }
    if (currentDocumentIndex < selectedTemplates.length - 1) {
      setCurrentDocumentIndex(currentDocumentIndex + 1);
      setCurrentPage(1);
    } else {
      setStep(2);
    }
  };

  const handlePreviousDocument = () => {
    if (currentDocumentIndex > 0) {
      setCurrentDocumentIndex(currentDocumentIndex - 1);
      setCurrentPage(1);
    } else {
      setStep(0);
    }
  };

  const handleNextPage = () => {
    const currentTemplate = getCurrentTemplate();
    const currentPages = documentPages[currentTemplate] || [];
    if (currentPage < currentPages.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSave = async () => {
    if (!lead || selectedTemplates.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      for (const templateName of selectedTemplates) {
        const templateFieldValues: Record<string, any> = { ...fieldValues };
        const templateFields = allFields[templateName] || [];
        
        // Ensure all required fields are present (even if empty)
        templateFields.forEach(field => {
          if (field.required && !(field.name in templateFieldValues)) {
            templateFieldValues[field.name] = '';
          }
        });
        
        // Save to database
        const hasFileUpload = templateFieldValues.documentos_anexos && templateFieldValues.documentos_anexos instanceof File;
        
        let response;
        if (hasFileUpload) {
          // Use FormData for file upload
          const formData = new FormData();
          formData.append('lead_id', lead.id.toString());
          formData.append('documentos_anexos', templateFieldValues.documentos_anexos);
          
          // Add other form fields
          Object.keys(templateFieldValues).forEach(key => {
            if (key !== 'documentos_anexos' && templateFieldValues[key] !== null && templateFieldValues[key] !== undefined) {
              formData.append(key, templateFieldValues[key].toString());
            }
          });
          
          response = await fetchWithCsrf('/api/contrato-adesao-nos/save', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
            },
            body: formData
          });
        } else {
          // Use JSON for regular data
          response = await fetchWithCsrf('/api/contrato-adesao-nos/save', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('jwt_token')}`
            },
            body: JSON.stringify({
              lead_id: lead.id,
              form_data: templateFieldValues
            })
          });
        }
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(`Failed to save form data: ${errorData.error || response.statusText}`);
        }
      }
      
      // Go to email step or completion
      if (availableDocs.length > 0) {
        setStep(4); // Go to email step
      } else {
        setStep(5); // Go directly to completion
      }
      if (onSave) onSave();
    } catch (e) {
      setError('Erro ao guardar dados do formulário.');
    } finally {
      setLoading(false);
    }
  };

  // Email step logic
  const handleEmailFieldChange = (field: string, value: any) => {
    setEmailStep(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailDocChange = (values: string[]) => {
    setEmailStep(prev => ({ ...prev, documentos: values }));
  };

  const handleEmailFileChange = (info: any) => {
    // Always use the current fileList and extract originFileObj for each
    setEmailFiles(info.fileList.map((f: any) => f.originFileObj).filter(Boolean));
  };

  const buildEmailBody = () => {
    return `Login Comercial Tablet:\nambaptista \nCoimbra_02\n\nContacto do comercial responsável:\n916155950\n\nNome de cliente:\n${lead?.name || ''}\n\nMorada:\n${emailStep.morada}\n\nContacto Cliente:\n${emailStep.contacto}\n\nDocumentos a assinar:\n${(emailStep.documentos || []).join('\n')}\n\nNotas:\n${emailStep.notas}`;
  };

  const handleEmailPreview = () => {
    setEmailPreview(buildEmailBody());
    setStep(6);
  };

  // Helper to format bytes as MB/KB
  const formatBytes = (bytes: number) => {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return bytes + ' B';
  };

  // Calculate total attachment size (uploaded files only)
  const getTotalAttachmentSize = () => {
    let total = 0;
    emailFiles.forEach(f => { if (f && f.size) total += f.size; });
    return total;
  };
  const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024; // 20 MB

  const handleSendEmail = async () => {
    const totalSize = getTotalAttachmentSize();
    if (totalSize > MAX_ATTACHMENT_SIZE) {
      message.error('O tamanho total dos anexos excede o limite permitido (20 MB). Remova alguns ficheiros e tente novamente.');
      return;
    }
    setSending(true);
    try {
      const res = await apiService.sendEmailWithAttachments({
        subject: emailStep.subject,
        body: buildEmailBody(),
        files: emailFiles
      });
      if (res.success) {
        message.success('E-mail enviado com sucesso!');
        setStep(7);
      } else {
        message.error(res.message || 'Falha ao enviar o e-mail.');
      }
    } catch (e) {
      message.error('Erro ao enviar o e-mail.');
    } finally {
      setSending(false);
    }
  };

  const getSteps = () => [
    { title: 'Seleção' },
    { title: 'Campos' },
    { title: 'Revisão' },
    { title: 'Guardado' },
    { title: 'Gerar E-mail' },
    { title: 'Confirmação' }
  ];

  const canGoToEmail = availableDocs.length > 0;

  // Get user data for autofill
  const { user } = useCognitoAuth();
  
  // Autofill function
  const getAutofillValue = (field: any) => {
    if (!field.autofill) return '';
    
    switch (field.autofill) {
      case 'user_name':
        return user?.username || '';
      case 'lead_name':
        return lead?.name || '';
      case 'lead_nif':
        return lead?.dif || '';
      case 'lead_telefone':
        return lead?.phone || '';
      case 'lead_email':
        return lead?.email || '';
      case 'lead_cidade':
        return lead?.city || '';
      case 'lead_codigo_postal_1':
        if (lead?.postal_code) {
          return lead.postal_code.split('-')[0] || '';
        }
        return '';
      case 'lead_codigo_postal_2':
        if (lead?.postal_code) {
          return lead.postal_code.split('-')[1] || '';
        }
        return '';
      default:
        return '';
    }
  };

  // Render Google Form Survey page with autofill functionality
  const renderGoogleFormSurveyPage = (pageFields: any[], currentPage: number, totalPages: number) => {
    // Special handling for postal code and localidade fields to be on the same line
    const postalCodeFields = pageFields.filter(f => f.name === 'codigo_postal_1' || f.name === 'codigo_postal_2');
    const localidadeField = pageFields.find(f => f.name === 'localidade');
    const otherFields = pageFields.filter(f => !['codigo_postal_1', 'codigo_postal_2', 'localidade'].includes(f.name));

    return (
      <Form form={form} layout="vertical">
        {otherFields.map((field, idx) => {
          // Handle autofill
          const autofillValue = getAutofillValue(field);
          if (autofillValue && !fieldValues[field.name]) {
            handleFieldChange(field.name, autofillValue);
          }

          // File upload for documentos_anexos field
          if (field.name === 'documentos_anexos') {
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Upload
                  beforeUpload={() => false}
                  onChange={(info) => {
                    if (info.fileList.length > 0) {
                      handleFieldChange(field.name, info.fileList[0].originFileObj);
                    } else {
                      handleFieldChange(field.name, null);
                    }
                  }}
                  fileList={fieldValues[field.name] ? [{
                    uid: '1',
                    name: fieldValues[field.name].name || 'documento.pdf',
                    status: 'done',
                    originFileObj: fieldValues[field.name]
                  }] : []}
                  accept=".pdf"
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>
                    Selecionar PDF
                  </Button>
                </Upload>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Apenas ficheiros PDF são permitidos
                </Text>
              </Form.Item>
            );
          }

          // Handle different field types
          if (field.type === 'select') {
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Select
                  mode={field.multiple ? 'multiple' : undefined}
                  value={fieldValues[field.name] || (field.multiple ? [] : '')}
                  onChange={(value) => handleFieldChange(field.name, value)}
                  placeholder={`Selecione ${field.label}`}
                  allowClear
                  maxTagCount={field.multiple ? 'responsive' : undefined}
                >
                  {field.options?.map((option: string) => (
                    <Option key={option} value={option}>{option}</Option>
                  ))}
                </Select>
              </Form.Item>
            );
          }

          if (field.type === 'checkbox') {
            return (
              <Form.Item key={field.name} valuePropName="checked" required={field.required}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <Checkbox
                    checked={fieldValues[field.name] || false}
                    onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                  >
                    {field.label}
                  </Checkbox>
                  {field.rental_info && (
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                      [{field.rental_info}]
                    </Text>
                  )}
                </div>
              </Form.Item>
            );
          }

          if (field.type === 'textarea') {
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Input.TextArea
                  value={fieldValues[field.name] || ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder || `Introduza ${field.label}`}
                  rows={4}
                />
              </Form.Item>
            );
          }

          if (field.type === 'email') {
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Input
                  type="email"
                  value={fieldValues[field.name] || ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  placeholder={`Introduza ${field.label}`}
                />
              </Form.Item>
            );
          }

          if (field.type === 'file_upload') {
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Upload
                  beforeUpload={() => false}
                  onChange={(info) => {
                    if (info.fileList.length > 0) {
                      handleFieldChange(field.name, info.fileList[0].originFileObj);
                    } else {
                      handleFieldChange(field.name, null);
                    }
                  }}
                  fileList={fieldValues[field.name] ? [{
                    uid: '1',
                    name: fieldValues[field.name].name || 'documento.pdf',
                    status: 'done',
                    originFileObj: fieldValues[field.name]
                  }] : []}
                  accept=".pdf"
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>
                    Selecionar PDF
                  </Button>
                </Upload>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Apenas ficheiros PDF são permitidos
                </Text>
              </Form.Item>
            );
          }

          // Handle validation for text fields
          if (field.validation) {
            const { pattern, message, maxLength } = field.validation;
            return (
              <Form.Item key={field.name} label={field.label} required={field.required}>
                <Input
                  value={fieldValues[field.name] || ''}
                  maxLength={maxLength}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (pattern === '^[0-9]{9}$') {
                      val = val.replace(/[^0-9]/g, '');
                      if (val.length > 9) val = val.slice(0, 9);
                    } else if (pattern === '^[0-9]{4}$') {
                      val = val.replace(/[^0-9]/g, '');
                      if (val.length > 4) val = val.slice(0, 4);
                    } else if (pattern === '^[0-9]{3}$') {
                      val = val.replace(/[^0-9]/g, '');
                      if (val.length > 3) val = val.slice(0, 3);
                    } else if (pattern === '^PT[0-9]{23}$') {
                      val = val.replace(/[^PT0-9]/g, '');
                      if (val.length > 25) val = val.slice(0, 25);
                    }
                    handleFieldChange(field.name, val);
                  }}
                  placeholder={`Introduza ${field.label}`}
                />
                {message && (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                    {message}
                  </Text>
                )}
              </Form.Item>
            );
          }

          // Default text field
          return (
            <Form.Item key={field.name} label={field.label} required={field.required}>
              <Input
                value={fieldValues[field.name] || ''}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                placeholder={`Introduza ${field.label}`}
              />
            </Form.Item>
          );
        })}
        
        {/* Special layout for postal code and localidade fields */}
        {(postalCodeFields.length > 0 || localidadeField) && (
          <Row gutter={16}>
            {postalCodeFields.map((field) => {
              const autofillValue = getAutofillValue(field);
              if (autofillValue && !fieldValues[field.name]) {
                handleFieldChange(field.name, autofillValue);
              }
              
              return (
                <Col span={8} key={field.name}>
                  <Form.Item label={field.label} required={field.required}>
                    <Input
                      value={fieldValues[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      placeholder={field.placeholder || `Introduza ${field.label}`}
                      maxLength={field.name === 'codigo_postal_1' ? 4 : 3}
                    />
                  </Form.Item>
                </Col>
              );
            })}
            {localidadeField && (
              <Col span={8}>
                {(() => {
                  const autofillValue = getAutofillValue(localidadeField);
                  if (autofillValue && !fieldValues[localidadeField.name]) {
                    handleFieldChange(localidadeField.name, autofillValue);
                  }
                  
                  return (
                    <Form.Item label={localidadeField.label} required={localidadeField.required}>
                      <Input
                        value={fieldValues[localidadeField.name] || ''}
                        onChange={(e) => handleFieldChange(localidadeField.name, e.target.value)}
                        placeholder={`Introduza ${localidadeField.label}`}
                      />
                    </Form.Item>
                  );
                })()}
              </Col>
            )}
          </Row>
        )}
      </Form>
    );
  };

  const renderStepContent = () => {
    if (step === 0) {
      return (
        <div>
          <Title level={4}>Selecione o modelo de documento NOS Securitas</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Preencha o formulário para guardar os dados
          </Text>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Escolha um ou mais documentos"
            onChange={handleTemplateSelect}
            value={selectedTemplates}
            maxTagCount="responsive"
          >
            {NOS_SECURITAS_DOC_TEMPLATES.map((tpl) => (
              <Option key={tpl.name} value={tpl.name}>{tpl.display_name}</Option>
            ))}
          </Select>
          <Space style={{ marginTop: 16 }}>
            <Button onClick={handlePreviousStep}>Anterior</Button>
            <Button type="primary" onClick={handleNextStep} disabled={selectedTemplates.length === 0}>
              Próximo
            </Button>
            {canGoToEmail && (
              <Button type="default" onClick={() => setStep(4)}>
                Ir para E-mail
              </Button>
            )}
          </Space>
        </div>
      );
    }
    if (step === 1) {
      const currentTemplate = getCurrentTemplate();
      const currentFields = getCurrentFields();
      const currentPages = documentPages[currentTemplate] || [];
      if (!currentPages || currentPages.length === 0) {
        return <Alert message="Erro: Não há páginas configuradas para este documento." type="error" showIcon />;
      }
      const currentPageConfig = currentPages.find(p => p.page_number === currentPage) || currentPages[0];
      const pageFields = currentPageConfig ? currentFields.filter(field => currentPageConfig.field_names.includes(field.name)) : currentFields;
      const totalPages = currentPages.length;
      return (
        <Form layout="vertical">
          <Title level={4}>
            Documento {currentDocumentIndex + 1} de {selectedTemplates.length}: {NOS_SECURITAS_DOC_TEMPLATES.find(t => t.name === currentTemplate)?.display_name || currentTemplate}
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Preencha os campos para o documento "{NOS_SECURITAS_DOC_TEMPLATES.find(t => t.name === currentTemplate)?.display_name || currentTemplate}"
          </Text>
          <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Text strong>Página {currentPage} de {totalPages}: {currentPageConfig?.page_name}</Text>
            {currentPageConfig?.page_description && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{currentPageConfig.page_description}</Text>
              </div>
            )}
          </div>
          {(() => {
            // Custom layout for Google Form Survey
            if (currentTemplate === 'google_form_survey') {
              return renderGoogleFormSurveyPage(pageFields, currentPage, currentPages.length);
            }
            // Custom layout for page 2 (Produtos e Serviços)
            if (currentPage === 2 && currentTemplate === 'contrato_adesao_nos_securitas') {
              const pacoteComercialField = pageFields.find(f => f.name === 'pacote_comercial');
              const equipamentosObrigatorios = pageFields.filter(f => f.name.startsWith('equipamento_obrigatorio_'));
              const outrosEquipamentosField = pageFields.find(f => f.name === 'outros_equipamentos');
              const validarNecessidades = pageFields.filter(f => f.name.startsWith('validar_necessidades_'));
              
              return (
                <>
                  {/* Pacote comercial */}
                  {pacoteComercialField && (
                    <Form.Item key={pacoteComercialField.name} label={pacoteComercialField.label} required={pacoteComercialField.required}>
                      <Select
                        value={fieldValues[pacoteComercialField.name] || ''}
                        onChange={(value) => handleFieldChange(pacoteComercialField.name, value)}
                        placeholder={`Selecione ${pacoteComercialField.label}`}
                        allowClear
                      >
                        {pacoteComercialField.options?.map((option: string) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  
                  {/* Equipamentos obrigatórios */}
                  {equipamentosObrigatorios.length > 0 && (
                    <div style={{ marginTop: 24, marginBottom: 16 }}>
                      <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 12 }}>Equipamentos obrigatórios:</Text>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {equipamentosObrigatorios.map((field) => {
                          // Auto-check mandatory equipment if not already set
                          const isChecked = fieldValues[field.name] !== undefined 
                            ? fieldValues[field.name] 
                            : (field.default_checked === true);
                          if (fieldValues[field.name] === undefined && field.default_checked === true) {
                            handleFieldChange(field.name, true);
                          }
                          return (
                            <div key={field.name} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                              >
                                {field.label}
                              </Checkbox>
                              {field.rental_info && (
                                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                                  [{field.rental_info}]
                                </Text>
                              )}
                            </div>
                          );
                        })}
                      </Space>
                    </div>
                  )}
                  
                  {/* Outros Equipamentos */}
                  {outrosEquipamentosField && (
                    <Form.Item key={outrosEquipamentosField.name} label={outrosEquipamentosField.label} required={outrosEquipamentosField.required}>
                      <Select
                        mode="multiple"
                        value={fieldValues[outrosEquipamentosField.name] || []}
                        onChange={(value) => handleFieldChange(outrosEquipamentosField.name, value)}
                        placeholder="Escolher equipamentos (um ou mais)"
                        allowClear
                        maxTagCount="responsive"
                      >
                        {outrosEquipamentosField.options?.map((option: string) => (
                          <Option key={option} value={option}>{option}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  
                  {/* Valide as necessidades do serviço de segurança */}
                  {validarNecessidades.length > 0 && (
                    <div style={{ marginTop: 24, marginBottom: 16 }}>
                      <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 12 }}>Valide as necessidades do serviço de segurança:</Text>
                      <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        {validarNecessidades.map((field) => (
                          <Checkbox
                            key={field.name}
                            checked={fieldValues[field.name] || false}
                            onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                            style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}
                          >
                            {field.label}
                          </Checkbox>
                        ))}
                      </Space>
                    </div>
                  )}
                </>
              );
            }
            // Default rendering for all other cases
            return pageFields.map((field, idx) => {
              // Handle autofill
              const autofillValue = getAutofillValue(field);
              if (autofillValue && !fieldValues[field.name]) {
                handleFieldChange(field.name, autofillValue);
              }

              // File upload for documentos_anexos field
              if (field.name === 'documentos_anexos') {
                return (
                  <Form.Item key={field.name} label={field.label} required={field.required}>
                    <Upload
                      beforeUpload={() => false}
                      onChange={(info) => {
                        if (info.fileList.length > 0) {
                          handleFieldChange(field.name, info.fileList[0].originFileObj);
                        } else {
                          handleFieldChange(field.name, null);
                        }
                      }}
                      fileList={fieldValues[field.name] ? [{
                        uid: '1',
                        name: fieldValues[field.name].name || 'documento.pdf',
                        status: 'done',
                        originFileObj: fieldValues[field.name]
                      }] : []}
                      accept=".pdf"
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>
                        Selecionar PDF
                      </Button>
                    </Upload>
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                      Apenas ficheiros PDF são permitidos
                    </Text>
                  </Form.Item>
                );
              }

              if (field.type === 'select') {
                return (
                  <Form.Item key={field.name} label={field.label} required={field.required}>
                    <Select
                      mode={field.multiple ? 'multiple' : undefined}
                      value={fieldValues[field.name] || (field.multiple ? [] : '')}
                      onChange={(value) => handleFieldChange(field.name, value)}
                      placeholder={`Selecione ${field.label}`}
                      allowClear
                      maxTagCount={field.multiple ? 'responsive' : undefined}
                    >
                      {field.options?.map((option: string) => (
                        <Option key={option} value={option}>{option}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }

              if (field.type === 'checkbox') {
                return (
                  <Form.Item key={field.name} valuePropName="checked" required={field.required}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <Checkbox
                        checked={fieldValues[field.name] || false}
                        onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                      >
                        {field.label}
                      </Checkbox>
                      {field.rental_info && (
                        <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                          [{field.rental_info}]
                        </Text>
                      )}
                    </div>
                  </Form.Item>
                );
              }

              if (field.type === 'textarea') {
                return (
                  <Form.Item key={field.name} label={field.label} required={field.required}>
                    <Input.TextArea
                      value={fieldValues[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      placeholder={field.placeholder || `Introduza ${field.label}`}
                      rows={4}
                    />
                  </Form.Item>
                );
              }

              if (field.type === 'email') {
                return (
                  <Form.Item key={field.name} label={field.label} required={field.required}>
                    <Input
                      type="email"
                      value={fieldValues[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      placeholder={`Introduza ${field.label}`}
                    />
                  </Form.Item>
                );
              }

              if (field.type === 'number') {
                return (
                  <Form.Item key={field.name} label={field.label} required={field.required}>
                    <Input
                      type="number"
                      value={fieldValues[field.name] || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                      placeholder={`Introduza ${field.label}`}
                    />
                  </Form.Item>
                );
              }

              // Handle validation for text fields
              if (field.validation) {
                const { pattern, message, maxLength } = field.validation;
                return (
                  <Form.Item key={field.name} label={field.label} required={field.required}>
                    <Input
                      value={fieldValues[field.name] || ''}
                      maxLength={maxLength}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (pattern === '^[0-9]{9}$') {
                          val = val.replace(/[^0-9]/g, '');
                          if (val.length > 9) val = val.slice(0, 9);
                        } else if (pattern === '^[0-9]{4}$') {
                          val = val.replace(/[^0-9]/g, '');
                          if (val.length > 4) val = val.slice(0, 4);
                        } else if (pattern === '^[0-9]{3}$') {
                          val = val.replace(/[^0-9]/g, '');
                          if (val.length > 3) val = val.slice(0, 3);
                        } else if (pattern === '^PT[0-9]{23}$') {
                          val = val.replace(/[^PT0-9]/g, '');
                          if (val.length > 25) val = val.slice(0, 25);
                        }
                        handleFieldChange(field.name, val);
                      }}
                      placeholder={`Introduza ${field.label}`}
                    />
                    {message && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                        {message}
                      </Text>
                    )}
                  </Form.Item>
                );
              }

              // Default text field
              return (
                <Form.Item key={field.name} label={field.label} required={field.required}>
                  <Input
                    value={fieldValues[field.name] || ''}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    placeholder={`Introduza ${field.label}`}
                  />
                </Form.Item>
              );
            });
          })()}
          <Space style={{ marginTop: 16 }}>
            <Button onClick={handlePreviousDocument}>{currentDocumentIndex === 0 ? 'Voltar' : 'Anterior'}</Button>
            <Button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Página Anterior</Button>
            <Button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Próxima Página</Button>
            <Button type="primary" onClick={handleNextDocument}>{currentDocumentIndex === selectedTemplates.length - 1 ? 'Concluir' : 'Próximo'}</Button>
          </Space>
        </Form>
      );
    }
    if (step === 2) {
      return (
        <div>
          <Title level={4}>Revisão dos Dados</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Revise os dados preenchidos antes de guardar
          </Text>
          {selectedTemplates.map((templateName, index) => {
            const templateObj = NOS_SECURITAS_DOC_TEMPLATES.find(t => t.name === templateName);
            const templateFields = allFields[templateName] || [];
            const filledFields = templateFields.filter(field => {
              const value = fieldValues[field.name];
              if (field.type === 'checkbox') {
                return value === true;
              }
              if (field.type === 'select' && field.multiple) {
                return Array.isArray(value) && value.length > 0;
              }
              return value !== undefined && value !== null && value !== '';
            });
            
            return (
              <Card key={templateName} style={{ marginBottom: 16 }}>
                <Title level={5}>Documento {index + 1}: {templateObj?.display_name || templateName}</Title>
                <Text type="secondary">
                  {filledFields.length} de {templateFields.length} campos preenchidos
                </Text>
                {filledFields.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong>Campos preenchidos:</Text>
                    <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                      {filledFields.slice(0, 10).map(field => {
                        const value = fieldValues[field.name];
                        let displayValue = value;
                        if (field.type === 'checkbox') {
                          displayValue = value ? 'Sim' : 'Não';
                        } else if (field.type === 'select' && field.multiple && Array.isArray(value)) {
                          displayValue = value.join(', ');
                        }
                        return (
                          <li key={field.name}>
                            <Text type="secondary">
                              {field.label || field.name}: {displayValue}
                            </Text>
                          </li>
                        );
                      })}
                      {filledFields.length > 10 && (
                        <li>
                          <Text type="secondary">... e mais {filledFields.length - 10} campos</Text>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </Card>
            );
          })}
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(1)}>Voltar</Button>
            <Button type="primary" onClick={handleSave} disabled={loading}>
              {loading ? <Spin size="small" /> : 'Guardar Dados'}
            </Button>
          </Space>
        </div>
      );
    }
    if (step === 3) {
      return (
        <div>
          <Alert
            message="Dados guardados com sucesso!"
            description="Os dados do formulário foram guardados na base de dados e podem ser consultados posteriormente."
            type="success"
            showIcon
          />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(2)}>Voltar</Button>
            {canGoToEmail && (
              <Button type="primary" onClick={() => setStep(4)}>
                Gerar E-mail
              </Button>
            )}
          </Space>
        </div>
      );
    }
    if (step === 4) {
      return (
        <Form layout="vertical">
          <Title level={4}>Personalizar E-mail</Title>
          <Form.Item label="Assunto">
            <Input
              value={emailStep.subject}
              onChange={e => handleEmailFieldChange('subject', e.target.value)}
              placeholder="Assunto do e-mail"
            />
          </Form.Item>
          <Form.Item label="Morada do Cliente">
            <Input
              value={emailStep.morada}
              onChange={e => handleEmailFieldChange('morada', e.target.value)}
              placeholder="Morada do cliente"
            />
          </Form.Item>
          <Form.Item label="Contacto Cliente">
            <Input
              value={emailStep.contacto}
              onChange={e => handleEmailFieldChange('contacto', e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="Contacto (apenas números)"
              maxLength={15}
            />
          </Form.Item>
          <Form.Item label="Documentos a assinar">
            <Select
              mode="multiple"
              value={emailStep.documentos}
              onChange={handleEmailDocChange}
              placeholder="Selecione os documentos"
            >
              {EMAIL_DOC_OPTIONS.map(opt => (
                <Option key={opt} value={opt}>{opt}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Notas">
            <Input.TextArea
              value={emailStep.notas}
              onChange={e => handleEmailFieldChange('notas', e.target.value)}
              placeholder="Notas adicionais"
              rows={3}
            />
          </Form.Item>

          <Form.Item label="Anexar ficheiros">
            <Upload
              multiple
              beforeUpload={() => false}
              onChange={handleEmailFileChange}
              fileList={emailFiles.map(f => ({ uid: f.name, name: f.name, status: 'done', size: f.size }))}
              onRemove={file => {
                setEmailFiles(files => files.filter(f => f.name !== file.name));
                return true;
              }}
            >
              <Button icon={<UploadOutlined />}>Selecionar ficheiros</Button>
            </Upload>
            <Text type="secondary">Pode anexar ficheiros do seu PC.</Text>
            <ul style={{ marginTop: 8 }}>
              {emailFiles.map((file, idx) => (
                <li key={file.name || idx}>{file.name} <Text type="secondary">({formatBytes(file.size)})</Text>{!file.lastModified && <Text type="secondary"> (não será restaurado após sair)</Text>}</li>
              ))}
            </ul>
          </Form.Item>
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(3)}>Voltar</Button>
            <Button type="primary" htmlType="button" onClick={e => { handleEmailPreview(); }}>
              Pré-visualizar E-mail
            </Button>
          </Space>
        </Form>
      );
    }
    if (step === 5) {
      return (
        <div>
          <Alert
            message="Dados guardados com sucesso!"
            description="Os dados do formulário foram guardados na base de dados e podem ser consultados posteriormente."
            type="success"
            showIcon
          />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={onClose} icon={<CloseOutlined />}>Fechar</Button>
          </Space>
        </div>
      );
    }
    if (step === 6) {
      return (
        <div>
          <Title level={4}>Pré-visualização do E-mail</Title>
          <pre style={{ background: '#f5f5f5', padding: 16, borderRadius: 6, minHeight: 200 }}>{emailPreview}</pre>
          <div style={{ marginTop: 16 }}>
            <Text strong>Ficheiros anexados:</Text>
            <ul>
              {emailFiles.map((file, idx) => (
                <li key={file.name || idx}>{file.name} <Text type="secondary">({formatBytes(file.size)})</Text></li>
              ))}
              {emailFiles.length === 0 && (
                <li><Text type="secondary">Nenhum ficheiro anexado</Text></li>
              )}
            </ul>
          </div>
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(4)}>Voltar</Button>
            <Button type="primary" loading={sending} onClick={handleSendEmail}>
              Confirmar e Enviar
            </Button>
          </Space>
        </div>
      );
    }
    if (step === 7) {
      return (
        <div>
          <Alert
            message="E-mail enviado com sucesso!"
            description="O e-mail foi enviado com sucesso."
            type="success"
            showIcon
          />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={onClose} icon={<CloseOutlined />}>Fechar</Button>
          </Space>
        </div>
      );
    }
    return null;
  };

  // Key for localStorage
  const EMAIL_DRAFT_KEY = lead ? `nos_securitas_email_draft_lead_${lead.id}` : 'nos_securitas_email_draft';

  // Save email step info to localStorage whenever it changes
  useEffect(() => {
    if (!open || !lead) return;
    const draft = {
      emailStep,
      emailFilesMeta: emailFiles.map(f => ({ name: f.name, size: f.size, lastModified: f.lastModified })), // Only meta, not file data
    };
    localStorage.setItem(EMAIL_DRAFT_KEY, JSON.stringify(draft));
  }, [emailStep, emailFiles, open, lead]);

  // Restore email step info from localStorage when modal opens
  useEffect(() => {
    if (open && lead) {
      const draftRaw = localStorage.getItem(EMAIL_DRAFT_KEY);
      if (draftRaw) {
        try {
          const draft = JSON.parse(draftRaw);
          if (draft.emailStep) setEmailStep(draft.emailStep);
          // Note: We cannot restore actual File objects, so just show meta in UI
        } catch {}
      }
    }
  }, [open, lead]);

  // Clear draft on successful send or modal close
  useEffect(() => {
    if (!open && lead) {
      localStorage.removeItem(EMAIL_DRAFT_KEY);
    }
    if (step === 7 && lead) {
      localStorage.removeItem(EMAIL_DRAFT_KEY);
    }
  }, [open, step, lead]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Formulário NOS Securitas"
      width={700}
      destroyOnClose
    >
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <Steps 
        current={step} 
        style={{ marginBottom: 24 }}
        items={getSteps().map((stepItem, index) => ({
          key: index,
          title: stepItem.title
        }))}
      />
      {loading ? <Spin /> : renderStepContent()}
    </Modal>
  );
};

export default NOSSecuritasDocumentWizard;
